import { Link } from "react-router-dom";
import { Phone, Search, HeartHandshake, Truck, KeyRound } from "lucide-react";
import { usePageMeta } from "../hooks/usePageMeta";
import { CtaBand } from "../components/CtaBand";
import { SITE, IMAGES } from "../data/site";

const PROCESS = [
  {
    icon: Search,
    title: "Walkthrough & plan",
    text: "We walk the property with you and agree on what stays, what sells, what's donated, and what goes. Then you get a clear written quote.",
  },
  {
    icon: HeartHandshake,
    title: "Sorting with care",
    text: "For downsizing, we work at your pace — patient help deciding what fits the next home, and what deserves a new one. Nothing is tossed that you wanted kept.",
  },
  {
    icon: Truck,
    title: "Removal & donation",
    text: "Our crew handles the heavy lifting. Donatable items go to local charities first; recyclables next; disposal only for what's left.",
  },
  {
    icon: KeyRound,
    title: "Broom-clean finish",
    text: "We leave the property empty and tidy — ready to list, rent, or hand over the keys.",
  },
];

const WHO = [
  "Families clearing a home after a loss or a move",
  "Seniors moving to a smaller home or community — and their adult children",
  "Executors and attorneys preparing a property for sale",
  "Real estate agents getting a listing market-ready",
];

const SITUATIONS = [
  "A whole house that needs to be empty by a settlement date",
  "Decades of belongings to sort before a move",
  "A cleanout after an estate sale ends",
  "Helping a parent downsize from across the country",
];

export default function CleanoutsPage() {
  usePageMeta(
    "Estate Cleanouts & Downsizing | MacWay Estate Services — DC, MD & VA",
    "Respectful estate cleanouts and patient downsizing help across DC, Maryland, and Virginia. Donation-first, broom-clean finish, written quote before work begins. Call (301) 500-0128."
  );

  return (
    <main data-testid="cleanouts-page">
      <section className="relative py-24 md:py-32 flex items-center" data-testid="cleanouts-hero">
        <img src={IMAGES.cleanout} alt="A MacWay truck ready for an estate cleanout" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-ink/90 via-ink/60 to-ink/30" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="max-w-2xl">
            <p className="eyebrow-label">Cleanouts & downsizing</p>
            <h1 className="mt-4 text-5xl sm:text-6xl font-semibold tracking-tight text-ivory leading-[1.05]" data-testid="cleanouts-headline">
              From full house to fresh start
            </h1>
            <p className="mt-6 text-lg text-ivory/85 leading-relaxed">
              Whether you're emptying a property on a deadline or helping a loved one into a smaller home, MacWay
              brings the people, the plan, and the patience to get it done right.
            </p>
            <div className="mt-9 flex flex-col sm:flex-row gap-4">
              <Link to="/contact" className="btn-brass" data-testid="cleanouts-quote-cta">Request a Quote</Link>
              <a href={SITE.phoneHref} className="btn-outline-light" data-testid="cleanouts-call-cta">
                <Phone className="w-4 h-4" /> Call {SITE.phone}
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-ivory" data-testid="cleanouts-process">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <p className="eyebrow-label">Our process</p>
            <h2 className="mt-4 text-4xl sm:text-5xl font-medium tracking-tight text-ink">How cleanouts & downsizing work</h2>
          </div>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6">
            {PROCESS.map((p) => (
              <div key={p.title} className="bg-white border border-linen p-8">
                <p.icon className="w-8 h-8 text-brass" />
                <h3 className="mt-5 text-2xl font-medium text-ink">{p.title}</h3>
                <p className="mt-3 text-base text-ink/70 leading-relaxed">{p.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-sand" data-testid="cleanouts-who">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div>
            <p className="eyebrow-label">Who it helps</p>
            <h2 className="mt-4 text-3xl sm:text-4xl font-medium tracking-tight text-ink">Made for moments like these</h2>
            <ul className="mt-7 space-y-4">
              {WHO.map((w) => (
                <li key={w} className="flex items-start gap-3 text-base text-ink/75">
                  <span className="mt-1.5 w-2 h-2 bg-brass shrink-0" aria-hidden="true" />
                  {w}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <p className="eyebrow-label">Common situations</p>
            <h2 className="mt-4 text-3xl sm:text-4xl font-medium tracking-tight text-ink">If any of this sounds familiar</h2>
            <ul className="mt-7 space-y-4">
              {SITUATIONS.map((s) => (
                <li key={s} className="flex items-start gap-3 text-base text-ink/75">
                  <span className="mt-1.5 w-2 h-2 bg-forest shrink-0" aria-hidden="true" />
                  {s}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="py-14 md:py-16 bg-ivory" data-testid="cleanouts-crosslink">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-medium tracking-tight text-ink">Need the whole house emptied?</h2>
          <p className="mt-4 text-base text-ink/70 leading-relaxed">
            Our dedicated cleanout page covers paid whole-home and estate cleanouts, pricing factors, and a
            short estimate request form.
          </p>
          <Link to="/estate-cleanouts" className="btn-brass mt-7" data-testid="cleanouts-page-cleanouts-link">
            Estate Cleanouts & Estimates
          </Link>
        </div>
      </section>

      <CtaBand
        title="One call gets it moving."
        text="Tell us about the property and the timeline. We'll come take a look and give you a clear written quote — then handle the rest."
      />
    </main>
  );
}
